/**
 * API Service Layer
 * Centralized axios instance with authentication interceptors
 */
import axios from 'axios';

// API Configuration from environment variables
// An empty REACT_APP_API_BASE_URL is a deliberate "same origin" setting: the UAT
// image builds with it blank and nginx proxies /v1/, /ws/ and /admin/ to the
// backend. Use ?? so that empty string survives -- with || it collapsed to the
// localhost fallback and every deployed browser called its own machine.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
const API_VERSION = process.env.REACT_APP_API_VERSION || 'v1';

// Full API URL: {base_url}/{version}
// Backend URLs are: /v1/accounts/, /v1/subscriptions/, etc. (no /api/ prefix)
const API_URL = `${API_BASE_URL}/${API_VERSION}`;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user';
const SESSION_TOKEN_KEY = 'session_token';

/**
 * Token management utilities
 */
export const tokenUtils = {
  getAccessToken: () => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  getSessionToken: () => localStorage.getItem(SESSION_TOKEN_KEY),
  getUser: () => {
    const user = localStorage.getItem(USER_KEY);
    return user ? JSON.parse(user) : null;
  },
  
  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  },

  setSessionToken: (sessionToken) => {
    localStorage.setItem(SESSION_TOKEN_KEY, sessionToken);
  },
  
  setUser: (user) => {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },
  
  clearAll: () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(SESSION_TOKEN_KEY);
  },
  
  isAuthenticated: () => {
    return !!localStorage.getItem(ACCESS_TOKEN_KEY);
  },
};

// Request interceptor - add auth token and session token
api.interceptors.request.use(
  (config) => {
    const token = tokenUtils.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Send stable session UUID so the backend can validate this specific device.
    // If another device logs in, its session UUID replaces ours in the DB,
    // and subsequent requests from this device will get SESSION_EXPIRED.
    const sessionToken = tokenUtils.getSessionToken();
    if (sessionToken) {
      config.headers['X-Session-Token'] = sessionToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Skip token refresh logic for auth endpoints (login, register, etc.)
    const isAuthEndpoint = originalRequest.url?.includes('/accounts/login') || 
                           originalRequest.url?.includes('/accounts/register') ||
                           originalRequest.url?.includes('/accounts/token/refresh');
    
    // Check for SESSION_EXPIRED error - user was logged out from another device
    const errorCode = error.response?.data?.error?.code || error.response?.data?.code;
    if (error.response?.status === 401 && errorCode === 'SESSION_EXPIRED') {
      // Clear all tokens (access, refresh, session UUID) and redirect to login
      tokenUtils.clearAll();
      // Store a flag to show session expired message on login page
      sessionStorage.setItem('session_expired', 'true');
      window.location.href = '/login';
      return Promise.reject(error);
    }
    
    // If error is 401 and we haven't tried refreshing yet and it's not an auth endpoint
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      
      const refreshToken = tokenUtils.getRefreshToken();
      
      if (refreshToken) {
        try {
          // Try to refresh the token
          const response = await axios.post(`${API_URL}/accounts/token/refresh/`, {
            refresh: refreshToken,
          });
          
          const { access } = response.data;
          tokenUtils.setTokens(access, refreshToken);
          
          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${access}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed - clear tokens and redirect to login
          tokenUtils.clearAll();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token - redirect to login
        tokenUtils.clearAll();
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

/**
 * Extract error message from API response
 */
export const getErrorMessage = (error) => {
  if (error.response?.data) {
    const data = error.response.data;
    
    // Handle standardized APIResponse error format: { success: false, error: { code, message, status } }
    if (data.error?.message) {
      return data.error.message;
    }
    
    // Handle standardized APIResponse format with top-level message
    if (data.message) {
      return data.message;
    }
    
    // Handle validation errors
    if (data.errors) {
      const firstError = Object.values(data.errors)[0];
      if (Array.isArray(firstError)) {
        return firstError[0];
      }
      return firstError;
    }
    
    // Handle detail field (DRF default)
    if (data.detail) {
      return data.detail;
    }
    
    // Handle non_field_errors
    if (data.non_field_errors) {
      return data.non_field_errors[0];
    }
  }
  
  // Network or other errors
  if (error.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Auth API endpoints
 */
export const authAPI = {
  /**
   * Register a new user
   * @param {Object} data - { name, email, password, password_confirm, whatsapp }
   */
  register: (data) => api.post('/accounts/register/', data),
  
  /**
   * Login user
   * @param {Object} data - { email, password }
   */
  login: (data) => api.post('/accounts/login/', data),
  
  /**
   * Logout user
   * @param {string} refreshToken - The refresh token to invalidate
   */
  logout: (refreshToken) => api.post('/accounts/logout/', { refresh_token: refreshToken }),
  
  /**
   * Refresh access token
   * @param {string} refreshToken - The refresh token
   */
  refreshToken: (refreshToken) => api.post('/accounts/token/refresh/', { refresh: refreshToken }),
  
  /**
   * Request password reset
   * @param {string} email - User's email address
   */
  forgotPassword: (email) => api.post('/accounts/password-reset/', { email }),
  
  /**
   * Confirm password reset
   * @param {Object} data - { uid, token, new_password, new_password_confirm }
   */
  resetPassword: (data) => api.post('/accounts/password-reset/confirm/', data),
  
  /**
   * Change password (authenticated)
   * @param {Object} data - { old_password, new_password, new_password_confirm }
   */
  changePassword: (data) => api.post('/accounts/change-password/', data),
};

/**
 * Dashboard API endpoints
 */
export const dashboardAPI = {
  /**
   * Get dashboard data including profile, subscription, and KYC info
   */
  getDashboard: () => api.get('/core/dashboard/'),
};

/**
 * User Profile API endpoints
 */
export const profileAPI = {
  /**
   * Get current user's profile
   */
  getProfile: () => api.get('/accounts/profile/'),
  
  /**
   * Update user profile
   * @param {Object} data - { name, whatsapp, picture }
   */
  updateProfile: (data) => {
    // If there's a picture, use FormData
    if (data.picture instanceof File) {
      const formData = new FormData();
      Object.keys(data).forEach(key => {
        formData.append(key, data[key]);
      });
      return api.patch('/accounts/profile/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return api.patch('/accounts/profile/', data);
  },
  
  /**
   * Update KYC information
   * @param {Object} data - { pancard, dob, whatsapp }
   */
  updateKYC: (data) => api.patch('/accounts/kyc/', data),
  
  /**
   * Get KYC status
   */
  getKYCStatus: () => api.get('/accounts/kyc/status/'),
  
  /**
   * Change password
   * @param {Object} data - { old_password, new_password, new_password_confirm }
   */
  changePassword: (data) => api.post('/accounts/change-password/', data),
  
  /**
   * Delete account
   * @param {Object} data - { password, reason }
   */
  deleteAccount: (data) => api.post('/accounts/delete-account/', data),
};

/**
 * Session API endpoints
 */
export const sessionAPI = {
  /**
   * Validate current session
   * @param {string} sessionToken - The session token to validate
   */
  validateSession: (sessionToken) => api.get('/accounts/sessions/validate/', {
    params: { session_token: sessionToken },
  }),
};

/**
 * Performance Reports API endpoints
 */
export const reportsAPI = {
  /**
   * Get list of performance reports with pagination
   * @param {Object} params - { page, page_size }
   */
  getReports: (params = {}) => api.get('/reports/performance-reports/', { params }),
  
  /**
   * Get single performance report details
   * @param {number} id - Report ID
   */
  getReport: (id) => api.get(`/reports/performance-reports/${id}/`),
  
  /**
   * Get PDF blob for secure rendering (not direct download)
   * @param {number} id - Report ID
   * @returns {Promise} - Returns blob response for PDF.js rendering
   */
  getReportPDF: (id) => api.get(`/reports/performance-reports/${id}/download/`, {
    responseType: 'blob',
  }),
};

/**
 * Sample Report Lead Capture API endpoints
 */
export const sampleReportAPI = {
  /**
   * Submit lead details (Step 1)
   * For new users: creates lead and sends OTP
   * For registered users: sends report directly
   * @param {Object} data - { name, pan, email, whatsapp }
   */
  submitLead: (data) => api.post('/reports/submit-lead/', data),

  /**
   * Resend OTP for existing leads (Step 2)
   * @param {Object} data - { email }
   */
  sendOtp: (data) => api.post('/reports/send-otp/', data),

  /**
   * Verify OTP (Step 3)
   * @param {Object} data - { email, otp }
   */
  submitOtp: (data) => api.post('/reports/submit-otp/', data),

  /**
   * Send sample report after OTP verification (Step 4)
   * @param {Object} data - { email }
   */
  sendReport: (data) => api.post('/reports/sample-report/send-report/', data),
};

/**
 * ICA Announcements API endpoints (Important Corporate Announcements)
 */
export const announcementsAPI = {
  /**
   * Get list of ICA announcements with optional filters
   * @param {Object} params - { month, year, start_date, end_date }
   */
  getAnnouncements: (params = {}) => api.get('/announcements/ica/', { params }),
  
  /**
   * Get single announcement details
   * @param {number} id - Announcement ID
   */
  getAnnouncement: (id) => api.get(`/announcements/ica/${id}/`),
  
  /**
   * Download announcement PDF
   * @param {number} id - Announcement ID
   * @returns {Promise} - Returns blob response for download
   */
  downloadAnnouncement: (id) => api.get(`/announcements/ica/${id}/download/`, {
    responseType: 'blob',
  }),
};

/**
 * Live Announcements API endpoints (Filtered by user's stock/index selections)
 */
export const liveAnnouncementsAPI = {
  /**
   * Get list of live announcements filtered by user's selections
   * @param {Object} params - { company_name, category_name, start_date, end_date, search, page }
   */
  getAnnouncements: (params = {}) => api.get('/announcements/live/', { params }),
  
  /**
   * Get single live announcement details
   * @param {number} id - Announcement ID
   */
  getAnnouncement: (id) => api.get(`/announcements/live/${id}/`),
  
  /**
   * Get PDF URL for a live announcement
   * @param {number} id - Announcement ID
   */
  getPdfUrl: (id) => api.get(`/announcements/live/${id}/download/`),
  
  /**
   * Get user selections summary (stocks + indexes)
   */
  getUserSelections: () => api.get('/announcements/selections/'),

  /**
   * Get distinct category names available to the user
   */
  getCategories: () => api.get('/announcements/live/categories/'),
};

/**
 * Stock Search & Selection API endpoints
 */
export const stocksAPI = {
  /**
   * Search stocks with pagination
   * @param {Object} params - { q, page, limit }
   */
  searchStocks: (params = {}) => api.get('/announcements/stocks/search/', { params }),
  
  /**
   * Get user's selected stocks
   */
  getSelectedStocks: () => api.get('/announcements/stocks/selection/'),
  
  /**
   * Select or deselect stocks
   * @param {Object} data - { stock_ids: [1, 2, 3], action: 'select' | 'deselect' }
   */
  updateSelection: (data) => api.post('/announcements/stocks/selection/', data),
};

/**
 * Index Search & Selection API endpoints
 */
export const indexesAPI = {
  /**
   * Search indexes with pagination
   * @param {Object} params - { q, page, limit }
   */
  searchIndexes: (params = {}) => api.get('/announcements/indexes/search/', { params }),
  
  /**
   * Get user's selected indexes
   */
  getSelectedIndexes: () => api.get('/announcements/indexes/selection/'),
  
  /**
   * Select or deselect indexes
   * @param {Object} data - { index_ids: [1, 2, 3], action: 'select' | 'deselect' }
   */
  updateSelection: (data) => api.post('/announcements/indexes/selection/', data),
};

/**
 * Subscription API endpoints
 */
export const subscriptionAPI = {
  /**
   * Get user's subscription access context (current subscription status)
   * Returns: user_type, subscription details, free_tier, services access map
   */
  getSubscriptionAccess: () => api.get('/subscriptions/subscriptions/'),
  
  /**
   * Get user's complete subscription purchase history
   * Returns: history array with all past and current subscriptions
   */
  getSubscriptionHistory: () => api.get('/subscriptions/subscriptions/history/'),
  
  /**
   * Download invoice PDF for a subscription
   * @param {number} subscriptionId - The subscription ID
   * @returns {Promise} - Returns blob response for PDF download
   */
  downloadInvoice: (subscriptionId) => api.get(`/subscriptions/subscriptions/${subscriptionId}/invoice/`, {
    responseType: 'blob',
  }),
};

/**
 * Notifications API endpoints
 */
export const notificationsAPI = {
  /**
   * Get list of received notifications with pagination
   * @param {Object} params - { page, page_size }
   */
  getNotifications: (params = {}) => api.get('/notifications/received/', { params }),
  
  /**
   * Get notification detail by ID
   * @param {number} notificationId - The notification ID
   */
  getNotificationDetail: (notificationId) => api.get(`/notifications/received/${notificationId}/`),
  
  /**
   * Mark notification as read
   * @param {number} notificationId - The notification ID
   */
  markAsRead: (notificationId) => api.post(`/notifications/received/${notificationId}/mark-read/`),
  
  /**
   * Mark all notifications as read
   */
  markAllAsRead: () => api.post('/notifications/mark-all-read/'),
  
  /**
   * Get unread notification count
   */
  getUnreadCount: () => api.get('/notifications/unread-count/'),
};

/**
 * Testimonials API endpoints
 */
export const testimonialsAPI = {
  /**
   * Submit a testimonial/review
   * @param {Object} data - { title, content, rating }
   * If user already has a testimonial, it will be updated
   */
  writeTestimonial: (data) => api.post('/clients/write-testimonial/', data),
  
  /**
   * Get list of active testimonials (public)
   */
  getTestimonials: () => api.get('/clients/testimonials/'),
};

/**
 * Client Complaints API endpoints
 */
export const clientComplaintsAPI = {
  /**
   * Get list of client complaints
   */
  getComplaints: () => api.get('/clients/client-complaints/'),
  
  /**
   * Get detailed complaints with monthly/yearly analytics
   */
  getDetailedComplaints: () => api.get('/clients/detailed-complaints/'),
};

/**
 * Public API endpoints (no authentication required)
 */
export const publicAPI = {
  /**
   * Get all active pricing plans
   * Returns plans with categories: 'stock_recommendations' or 'live_corporate_announcements'
   */
  getPricingPlans: () => api.get('/subscriptions/pricing-plans/'),
  
  /**
   * Get monthly announcement summary (public, no auth required)
   * @param {number} months - Number of months to retrieve (default 12)
   */
  getAnnouncementMonthlySummary: (months = 12) => api.get('/announcements/ica/monthly-summary/', { params: { months } }),

  /**
   * Get public performance reports list
   * @param {Object} params - { page, page_size }
   */
  getPerformanceReports: (params = {}) => api.get('/reports/public/performance-reports/', { params }),

  /**
   * Submit lead capture form for performance report access
   * @param {Object} data - { name, email, whatsapp, report_id }
   */
  submitPerformanceReportLead: (data) => api.post('/reports/public/performance-reports/submit-lead/', data),

  /**
   * Get performance report PDF (after lead capture)
   * @param {number} reportId - Report ID
   * @param {string} email - Email used in lead capture form
   * @returns {Promise} - Returns blob response for PDF.js rendering
   */
  getPerformanceReportPDF: (reportId, email) => api.get(`/reports/public/performance-reports/${reportId}/view/`, {
    params: { email },
    responseType: 'blob',
  }),
};

/**
 * Payment API endpoints (for Razorpay integration)
 * Supports both guest and authenticated user flows
 */
export const paymentAPI = {
  /**
   * Validate a coupon code
   * @param {string} couponCode - The coupon code to validate
   * @param {number} planId - The plan ID to check category match
   * @returns {Promise} - { coupon_code, percentage, valid_until, applicable_to }
   */
  checkCoupon: (couponCode, planId) => api.post('/subscriptions/check-coupon/', { 
    coupon_code: couponCode,
    plan_id: planId
  }),
  
  /**
   * Calculate total charges including gateway fees and GST
   * @param {number} planId - The plan ID
   * @param {string} couponCode - Optional coupon code
   * @returns {Promise} - { plan_original_price, base_amount, gateway_charges, gst_amount, total_amount, coupon }
   */
  calculateCharges: (planId, couponCode = '') => api.post('/subscriptions/calculate-charges/', {
    plan_id: planId,
    coupon_code: couponCode,
  }),
  
  // ========== AUTHENTICATED USER ENDPOINTS ==========
  
  /**
   * Create Razorpay order for authenticated user
   * @param {Object} data - { plan_id, coupon_code, name, email, dob, whatsapp, pancard }
   * @returns {Promise} - { order_id, amount, razorpay_key, charges, plan, coupon }
   */
  createOrder: (data) => api.post('/subscriptions/razorpay/create-order/', data),
  
  /**
   * Verify payment for authenticated user
   * @param {Object} data - { razorpay_payment_id, razorpay_order_id, razorpay_signature, plan_id, amount, ... }
   * @returns {Promise} - { id, plan_name, payment_status, ... }
   */
  verifyPayment: (data) => api.post('/subscriptions/razorpay/verify-payment/', data),
  
  // ========== GUEST USER ENDPOINTS ==========
  
  /**
   * Create Razorpay order for guest user (creates temporary account)
   * @param {Object} data - { email, name, whatsapp, pancard, dob, plan_id, coupon_code }
   * @returns {Promise} - { order_id, amount, razorpay_key, user_id, temp_password, charges, plan, coupon }
   */
  guestCreateOrder: (data) => api.post('/subscriptions/guest/create-order/', data),
  
  /**
   * Verify payment for guest user
   * @param {Object} data - { razorpay_payment_id, razorpay_order_id, razorpay_signature, plan_id, user_id, temp_password, amount, ... }
   * @returns {Promise} - { id, plan_name, payment_status, account: { email, password, message } }
   */
  guestVerifyPayment: (data) => api.post('/subscriptions/guest/verify-payment/', data),
  
  // ========== FREE SUBSCRIPTION ENDPOINTS (Zero-amount) ==========
  
  /**
   * Activate free subscription for authenticated user (when coupon covers full amount)
   * @param {Object} data - { plan_id, coupon_code, name?, email?, dob?, whatsapp?, pancard? }
   * @returns {Promise} - { success: true, subscription: { id, plan_name, ... }, message }
   */
  activateFreeSubscription: (data) => api.post('/subscriptions/activate-free/', data),
  
  /**
   * Activate free subscription for guest user (creates account, no payment)
   * @param {Object} data - { email, name, whatsapp, pancard, dob, plan_id, coupon_code }
   * @returns {Promise} - { success: true, subscription: { id, plan_name, ... }, account: { email, password }, message }
   */
  guestActivateFreeSubscription: (data) => api.post('/subscriptions/guest/activate-free/', data),
};

// =============================================================================
// REWARD SPINNER API
// =============================================================================

/**
 * Reward Spinner API - Spin to win promotional feature
 */
export const spinnerAPI = {
  /**
   * Get spinner configuration (offers list)
   * @returns {Promise} - { offers: [{ segment, name, coupon_code, color }] }
   */
  getConfig: () => api.get('/subscriptions/spinner/config/'),
  
  /**
   * Check if device is eligible to spin
   * @param {string} deviceFingerprint - Unique device identifier
   * @returns {Promise} - { eligible: boolean, already_spun: boolean, coupon_code?: string }
   */
  checkEligibility: (deviceFingerprint) => api.post('/subscriptions/spinner/check/', { 
    device_fingerprint: deviceFingerprint 
  }),
  
  /**
   * Claim spin reward - submits lead and spins the wheel
   * @param {Object} data - { device_fingerprint, name, email, phone }
   * @returns {Promise} - { success, offer_name, coupon_code, discount_percentage, valid_until, segment, color }
   */
  claim: (data) => api.post('/subscriptions/spinner/claim/', data),
};

export default api;
